import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card/card';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import HStack from '@/components/Layout/HStack';
import { Text } from '@/components/Typography/Text';
import { useHover } from '@/hooks/useHover';
import { cn } from '@/lib/cn';
interface SectionCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** セクションのタイトル */
  title: ReactNode;
  /** セクションのアイコン */
  icon?: IconName;
  /** セクションの右上に表示されるアクションボタン */
  actionButton?: ReactNode;
  /** trueの場合、アクションボタンはホバー時のみ表示されます */
  hideActionButtonUntilHover?: boolean;
}

/**
 * セクションカードコンポーネント
 *
 * プロフィールページなどで、関連する情報をグループ化して表示するためのカードコンポーネントです。
 * タイトル、アイコン、およびオプションのアクションボタンを含むヘッダーと、
 * コンテンツエリアで構成されています。
 *
 * @example
 * ```tsx
 * <SectionCard
 *   title="部署情報"
 *   icon="NetworkIcon"
 *   hideActionButtonUntilHover
 *   actionButton={
 *     <ActionButton
 *       icon="PlusIcon"
 *       onClick={handleAddClick}
 *     >
 *       追加
 *     </ActionButton>
 *   }
 * >
 *   <DepartmentList />
 * </SectionCard>
 * ```
 */
const SectionCard = forwardRef<HTMLDivElement, SectionCardProps>(({
  title,
  icon,
  children,
  className,
  actionButton,
  hideActionButtonUntilHover = false,
  ...rest
}, ref) => {
  const {
    ref: hoverRef,
    isHovered
  } = useHover<HTMLDivElement>();
  return <Card ref={node => {
    if (typeof ref === 'function') ref(node);
    hoverRef(node);
  }} className={cn('bg-background/60', className)} {...rest}>
        <CardHeader>
          <CardTitle>
            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2 w-full">
                {icon && <Icon iconName={icon} strokeWidth={2} textColor="text-primary" colorVariant="primary" variant="l" />}
                {typeof title === 'string' ? <Text variant="h3">{title}</Text> : title}
              </HStack>
              {actionButton && <div className={cn('relative z-50',
          // NOTE: ダイアログ等のオーバーレイと追加ボタンの表示が重ならないよう、z-indexを設定
          // ホバー時のボタン表示をopacityで制御し、表示切り替え
          hideActionButtonUntilHover && ['transition-opacity', !isHovered ? 'opacity-0' : 'opacity-100'])}>
                  {actionButton}
                </div>}
            </HStack>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-7 max-md:grid-cols-1 gap-5 max-md:gap-3 max-md:p-4 max-md:pt-0">
          {children}
        </CardContent>
      </Card>;
});
SectionCard.displayName = 'SectionCard';
export default SectionCard;